import StyledTextFieldWithTitle from "@/modules/common/components/input/styled-text-field-with-title";
import { FinancialDetailsFormFields } from "@/modules/property-tracks/hooks/rental-financial-proposal/multi-step-form.hook";
import { Alert, Box, Divider, Typography } from "@mui/material";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";
import FinancialShare from "./financial-share";
import FinancialCostSharePayments from "./financial-cost-share-payments";
import { ProposalType } from "@/modules/property-tracks/types";
import { PROPOSAL_TYPE_OPTIONS } from "@/modules/property-tracks/constants";
import NwChip from "@/modules/common/components/ui-kit/chip";

const LOAN_AMOUNT_LIMIT = 5_000_000;
const LOAN_TERM_LIMIT = 5;


interface FinancialDetailsFormProps {
  form: UseFormReturn<FinancialDetailsFormFields>;
  totalAmount: number;
}

export default function FinancialDetailsForm({
  form,
  totalAmount,
}: FinancialDetailsFormProps) {
  const proposalType = useWatch({ control: form.control, name: "proposalType" });
  const isNawyNow = proposalType === ProposalType.NawyNow;

  const loanAmount = Number(useWatch({ control: form.control, name: "loanAmount" })) || 0;
  const loanTerm = Number(useWatch({ control: form.control, name: "loanTerm" })) || 0;
  const showDisclaimer = isNawyNow && (loanAmount > LOAN_AMOUNT_LIMIT || loanTerm > LOAN_TERM_LIMIT);

  const clientCostSharePercent = useWatch({
    control: form.control,
    name: "clientCostSharePercent",
  }) || 0;

  const clientCostShare = totalAmount * (clientCostSharePercent / 100);

  return (
    <Box>
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.md,
          mb: t.nawy.spacing.lg,
        })}
      >
        <Controller
          control={form.control}
          name="proposalType"
          render={({ field }) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
              >
                Proposal type
              </Typography>
              {PROPOSAL_TYPE_OPTIONS.map((option) => (
                <NwChip
                  key={option.value}
                  label={option.label}
                  color={field.value === option.value ? "primary" : "default"}
                  variant="outlined"
                  onClick={() => field.onChange(option.value)}
                  clickable
                />
              ))}
            </Box>
          )}
        />
        <Controller
          control={form.control}
          name="finishingMonths"
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Finishing months"
              placeholder="Finishing months"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: { endAdornment: <Typography>months</Typography> },
              }}
            />
          )}
        />
        {isNawyNow && (
          <>
            <Controller
              control={form.control}
              name="loanAmount"
              render={({ field, fieldState }) => (
                <StyledTextFieldWithTitle
                  {...field}
                  formatNumeric
                  title="Loan amount"
                  placeholder="Loan amount"
                  fullWidth
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  slotProps={{
                    input: { endAdornment: <Typography>EGP</Typography> },
                  }}
                />
              )}
            />
            <Controller
              control={form.control}
              name="loanTerm"
              render={({ field, fieldState }) => (
                <StyledTextFieldWithTitle
                  {...field}
                  formatNumeric
                  title="Loan term"
                  placeholder="Loan term"
                  fullWidth
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  slotProps={{
                    input: { endAdornment: <Typography>years</Typography> },
                  }}
                />
              )}
            />
            <Controller
              control={form.control}
              name="monthlyInstallment"
              render={({ field, fieldState }) => (
                <StyledTextFieldWithTitle
                  {...field}
                  formatNumeric
                  title="Monthly installment"
                  placeholder="Monthly installment"
                  fullWidth
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  slotProps={{
                    input: { endAdornment: <Typography>EGP</Typography> },
                  }}
                />
              )}
            />
            {showDisclaimer && (
              <Alert severity="warning">
                {loanAmount > LOAN_AMOUNT_LIMIT &&
                  `Loan amount exceeds the recommended limit of ${LOAN_AMOUNT_LIMIT.toLocaleString()} EGP. `}
                {loanTerm > LOAN_TERM_LIMIT &&
                  `Loan term exceeds the recommended limit of ${LOAN_TERM_LIMIT} years.`}
              </Alert>
            )}
          </>
        )}
      </Box>

      <Divider sx={(t) => ({ mb: t.nawy.spacing.lg })} />

      <Typography
        sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
      >
        Rental details
      </Typography>
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.md,
        })}
      >
        <Controller
          control={form.control}
          name={"rentalAmount"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Rental amount"
              placeholder="Rental amount"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>EGP</Typography>,
                },
              }}
            />
          )}
        />
        <Controller
          control={form.control}
          name={"annualIncreasePercent"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Annual increase"
              placeholder="Annual increase"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>%</Typography>,
                },
              }}
            />
          )}
        />
        <Controller
          control={form.control}
          name={"rentalYears"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Number of years"
              placeholder="Number of years"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>years</Typography>,
                },
              }}
            />
          )}
        />
      </Box>
      <Divider sx={(t) => ({ my: t.nawy.spacing.lg })} />

      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.md,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Cost & revenue splitting
        </Typography>
        <FinancialShare
          form={form}
          field={"clientCostSharePercent"}
          title={"Cost share split"}
        />
        <FinancialShare
          form={form}
          field={"clientRevenueSharePercent"}
          title={"Revenue share split"}
        />
      </Box>

      {!isNawyNow && (
        <>
          <Divider sx={(t) => ({ my: t.nawy.spacing.lg })} />
          <Box
            sx={(t) => ({
              display: "flex",
              flexDirection: "column",
              gap: t.nawy.spacing.md,
            })}
          >
            <Typography
              sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
            >
              Payment plan
            </Typography>
            <FinancialCostSharePayments
              form={form}
              clientTotalCostShare={clientCostShare}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
